import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import DashboardLayout from "../dashboard/_components/DashboardLayout";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
} from "firebase/firestore";
import {
  FileText,
  Search,
  Filter,
  Download,
  Eye,
  Calendar,
  IndianRupee,
  User,
  Phone,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  TrendingUp,
  DollarSign,
  Image as ImageIcon,
  X,
  CreditCard,
} from "lucide-react";

// The customer app creates personal loan applications with status "pending_review"
// (older records may use "pending", "submitted", "Pending" or no status at all).
// Normalize every not-yet-reviewed value to "pending" so stats, filters and badges
// all treat freshly submitted applications consistently.
const normalizeLoanStatus = (status) => {
  const s = (status || "").toLowerCase();
  return !s ||
    s === "pending" ||
    s === "pending_review" ||
    s === "pending-review" ||
    s === "submitted"
    ? "pending"
    : s;
};

export default function PersonalLoanApplications() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState(null);
  const [editingLoanId, setEditingLoanId] = useState(null);
  const [editedLoanAmount, setEditedLoanAmount] = useState("");
  const [savingLoanId, setSavingLoanId] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((u) => {
      if (u) {
        setUser(u);
      } else {
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchLoans();
    }
  }, [user]);

  const fetchLoans = async () => {
    setLoading(true);
    try {
      const loansRef = collection(db, "personalLoan");

      const q = query(loansRef, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);

      const loansData = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        loansData.push({
          id: doc.id,
          ...data,
          status: normalizeLoanStatus(data.status),
        });
      });

      setLoans(loansData);
    } catch (error) {
      // Fallback without ordering
      try {
        const loansRef = collection(db, "personalLoan");
        const querySnapshot = await getDocs(loansRef);

        const loansData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          loansData.push({
            id: doc.id,
            ...data,
            status: normalizeLoanStatus(data.status),
          });
        });

        setLoans(loansData);
      } catch (err) {
        // Silent fail
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter and search loans
  const filteredLoans = loans.filter((loan) => {
    const matchesSearch =
      loan.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.mobileNumber?.includes(searchTerm) ||
      loan.loanAmount?.toString().includes(searchTerm) ||
      loan.aadharNumber?.includes(searchTerm) ||
      loan.panNumber?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "all" || loan.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const handleViewDocuments = (loan) => {
    setSelectedDocuments(loan);
    setShowDocumentModal(true);
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLoans = filteredLoans.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredLoans.length / itemsPerPage);

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      }
      return new Date(timestamp).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };
 console.log(formatCurrency)
  
  const formatCompactAmount = (amount) => {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount === 0) {
      return "₹0";
    }

    const prefix = numericAmount < 0 ? "-₹" : "₹";
    const absValue = Math.abs(numericAmount);

    const formatValue = (value) => {
      if (value >= 100) {
        return Math.round(value).toString();
      }
      if (value >= 10) {
        return value.toFixed(1).replace(/\.0$/, "");
      }
      return value
        .toFixed(2)
        .replace(/\.00$/, "")
        .replace(/(\.\d)0$/, "$1");
    };

    if (absValue >= 10000000) {
      return `${prefix}${formatValue(absValue / 10000000)} Cr`;
    }

    if (absValue >= 100000) {
      return `${prefix}${formatValue(absValue / 100000)} Lakh`;
    }

    if (absValue >= 1000) {
      return `${prefix}${formatValue(absValue / 1000)} K`;
    }

    return prefix === "₹"
      ? formatCurrency(absValue)
      : `-${formatCurrency(absValue)}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      "under-review": "bg-blue-100 text-blue-800",
      disbursed: "bg-purple-100 text-purple-800",
      completed: "bg-gray-100 text-gray-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      approved: CheckCircle,
      rejected: XCircle,
      "under-review": AlertCircle,
      disbursed: DollarSign,
      completed: CheckCircle,
    };
    return icons[status] || Clock;
  };

  const handleExport = () => {
    const headers = [
      "Full Name",
      "Mobile",
      "Email",
      "Loan Amount",
      "Monthly Salary",
      "Purpose",
      "Aadhar",
      "PAN",
      "City",
      "State",
      "Status",
      "Applied Date",
    ];
    const csvData = filteredLoans.map((loan) => [
      loan.fullName || "N/A",
      loan.mobileNumber || "N/A",
      loan.email || "N/A",
      loan.loanAmount || "0",
      loan.monthlySalary || "N/A",
      loan.loanPurpose || "N/A",
      loan.aadharNumber || "N/A",
      loan.panNumber || "N/A",
      loan.city || "N/A",
      loan.state || "N/A",
      loan.status || "Pending",
      formatDate(loan.createdAt),
    ]);

    const csv = [
      headers.join(","),
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `personal-loans_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const total = loans.length;
    const pending = loans.filter(
      (l) => (l.status || "").toLowerCase() === "pending",
    ).length;
    const approved = loans.filter(
      (l) => (l.status || "").toLowerCase() === "approved",
    ).length;
    const rejected = loans.filter(
      (l) => (l.status || "").toLowerCase() === "rejected",
    ).length;
    const totalAmount = loans.reduce(
      (sum, l) => sum + (Number(l.loanAmount) || 0),
      0,
    );
    const now = new Date();
    const thisMonth = loans.filter((l) => {
      if (!l.createdAt) return false;
      const date = l.createdAt.toDate
        ? l.createdAt.toDate()
        : new Date(l.createdAt);
      return (
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
      );
    }).length;

    const instantRevenue = loans.reduce(
      (sum, l) =>
        sum +
        (l.instantApprovalFeePaid
          ? Number(l.instantApprovalPayment?.amount || 0)
          : 0),
      0,
    );
    const instant = loans.filter((l) => l.instantApprovalFeePaid).length;

    return {
      total,
      pending,
      approved,
      rejected,
      totalAmount,
      thisMonth,
      instantRevenue,
      instant,
    };
  }, [loans]);

  if (!user) return null;

  const handleSaveLoanAmount = async (loanId) => {
    if (!editedLoanAmount || isNaN(editedLoanAmount)) return;

    try {
      setSavingLoanId(loanId);

      await updateDoc(doc(db, "personalLoan", loanId), {
        loanAmount: Number(editedLoanAmount),
        updatedAt: new Date(),
        updatedBy: user.uid,
      });

      // Update local state (NO refetch)
      setLoans((prev) =>
        prev.map((l) =>
          l.id === loanId
            ? {
                ...l,
                loanAmount: Number(editedLoanAmount),
                updatedAt: new Date(),
              }
            : l,
        ),
      );

      setEditingLoanId(null);
      setEditedLoanAmount("");
    } catch (err) {
      console.error("Failed to update loan amount", err);
    } finally {
      setSavingLoanId(null);
    }
  };

  return (
    <DashboardLayout>
      {/* Header Section */}
      <div className="mb-4 sm:mb-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <div className="p-1.5 bg-green-600 rounded-lg">
                <FileText className="w-5 h-5 text-white" />
              </div>
              Personal Loans
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-1">
              Manage and review all personal loan applications
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchLoans}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline text-xs font-medium">
                Refresh
              </span>
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline text-xs font-medium">
                Export
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-5 sm:mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <p className="text-gray-600 text-xs font-medium">Total</p>
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
              {stats.total}
            </h3>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-gray-600 text-xs font-medium">
              Want Instant Approval
            </p>
            <div className="p-2 bg-yellow-100 rounded-lg">
              <CheckCircle className="w-4 h-4 text-yellow-600" />
            </div>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
            {stats.instant}
          </h3>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <p className="text-gray-600 text-xs font-medium">
                Instant Fees Collected
              </p>
              <div className="p-2 bg-yellow-100 rounded-lg">
                <IndianRupee className="w-4 h-4 text-yellow-600" />
              </div>
            </div>
            <h3 className="text-base sm:text-lg font-bold text-gray-900">
              {formatCompactAmount(stats.instantRevenue)}
            </h3>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <p className="text-gray-600 text-xs font-medium">Pending</p>
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-4 h-4 text-yellow-600" />
              </div>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
              {stats.pending}
            </h3>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <p className="text-gray-600 text-xs font-medium">Approved</p>
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
              {stats.approved}
            </h3>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <p className="text-gray-600 text-xs font-medium">Rejected</p>
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="w-4 h-4 text-red-600" />
              </div>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
              {stats.rejected}
            </h3>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <p className="text-gray-600 text-xs font-medium">This Month</p>
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="w-4 h-4 text-purple-600" />
              </div>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
              {stats.thisMonth}
            </h3>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4">
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <p className="text-gray-600 text-xs font-medium">Total Amount</p>
              <div className="p-2 bg-indigo-100 rounded-lg">
                <IndianRupee className="w-4 h-4 text-indigo-600" />
              </div>
            </div>
            <h3 className="text-base sm:text-lg font-bold text-gray-900">
              {formatCompactAmount(stats.totalAmount)}
            </h3>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4 mb-5">
        <div className="flex flex-col gap-2.5 sm:gap-3">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name, email, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 sm:py-2.5 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-400 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm"
            />
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400 shrink-0" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="flex-1 px-3 py-2 sm:py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="under-review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="disbursed">Disbursed</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Loans Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <RefreshCw className="w-8 h-8 text-green-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading loan applications...</p>
              </div>
            </div>
          ) : currentLoans.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 text-lg font-medium">
                  No loan applications found
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  {searchTerm
                    ? "Try adjusting your search terms"
                    : "Applications will appear here when submitted"}
                </p>
              </div>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Applicant
                    </th>
                    <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Loan Amount
                    </th>
                    <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                      Monthly Salary
                    </th>
                    <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden xl:table-cell">
                      Location
                    </th>
                    <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Documents
                    </th>
                    <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                      Applied Date
                    </th>
                    <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">
                      Status
                    </th>
                    <th className="px-4 sm:px-6 py-3 sm:py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {currentLoans.map((loan) => {
                    const StatusIcon = getStatusIcon(loan.status);
                    return (
                      <tr
                        key={loan.id}
                        className={`transition-colors hover:bg-gray-50/50 ${
                          loan.instantApprovalFeePaid ? "bg-yellow-50/50" : ""
                        }`}
                      >
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex flex-col leading-tight">
                              {loan.instantApprovalFeePaid && (
                                <span className="mb-0.5 w-fit text-[10px] font-bold bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full">
                                  Instant Approval
                                </span>
                              )}

                              <span className="font-semibold text-sm text-gray-900">
                                {loan.fullName || "N/A"}
                              </span>
                            </div>

                            <div className="min-w-0">
                              <div className="font-semibold text-sm text-gray-900">
                                {loan.fullName || "N/A"}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                                <Phone className="w-3 h-3" />
                                {loan.mobileNumber || "N/A"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <IndianRupee className="w-4 h-4 text-gray-500" />

                              {editingLoanId === loan.id ? (
                                <>
                                  <input
                                    type="number"
                                    value={editedLoanAmount}
                                    onChange={(e) =>
                                      setEditedLoanAmount(e.target.value)
                                    }
                                    className="w-28 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                  />

                                  <button
                                    onClick={() =>
                                      handleSaveLoanAmount(loan.id)
                                    }
                                    disabled={savingLoanId === loan.id}
                                    className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                  >
                                    {savingLoanId === loan.id
                                      ? "Saving..."
                                      : "Save"}
                                  </button>

                                  <button
                                    onClick={() => setEditingLoanId(null)}
                                    className="text-xs px-2 py-1 bg-gray-200 rounded hover:bg-gray-300"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <span className="text-sm font-bold text-gray-900">
                                    {loan.loanAmount
                                      ? Number(loan.loanAmount).toLocaleString(
                                          "en-IN",
                                        )
                                      : "0"}
                                  </span>

                                  {/* Optional safety: allow edit only when pending */}
                                  <button
                                    onClick={() => {
                                      setEditingLoanId(loan.id);
                                      setEditedLoanAmount(
                                        loan.loanAmount || "",
                                      );
                                    }}
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    Edit
                                  </button>
                                </>
                              )}
                            </div>

                            <span className="text-xs text-gray-500 mt-0.5 capitalize">
                              {loan.loanPurpose
                                ? loan.loanPurpose.substring(0, 20) +
                                  (loan.loanPurpose.length > 20 ? "..." : "")
                                : "No purpose"}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4 hidden lg:table-cell">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <IndianRupee className="w-3.5 h-3.5 text-gray-400" />
                            {loan.monthlySalary
                              ? Number(loan.monthlySalary).toLocaleString(
                                  "en-IN",
                                )
                              : "-"}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4 hidden xl:table-cell">
                          <div className="text-sm text-gray-600">
                            <div className="font-medium">
                              {loan.city || "N/A"}
                            </div>
                            <div className="text-xs text-gray-500">
                              {loan.state || "N/A"}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <button
                            onClick={() => handleViewDocuments(loan)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-xs font-medium"
                            title="View Documents"
                          >
                            <ImageIcon className="w-3.5 h-3.5" />
                            <span>View Docs</span>
                          </button>
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4 text-sm text-gray-500 hidden lg:table-cell">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {formatDate(loan.createdAt)}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4 hidden md:table-cell">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                              loan.status,
                            )}`}
                          >
                            <StatusIcon className="w-3.5 h-3.5" />
                            <span className="capitalize">
                              {loan.status?.replace("-", " ") || "Pending"}
                            </span>
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-3 sm:py-4">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() =>
                                navigate(`/personal-loans/${loan.id}`)
                              }
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="View Full Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-4 sm:px-6 py-4 border-t border-gray-100">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-sm text-gray-600">
                      Showing {indexOfFirstItem + 1} to{" "}
                      {Math.min(indexOfLastItem, filteredLoans.length)} of{" "}
                      {filteredLoans.length} applications
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(1, prev - 1))
                        }
                        disabled={currentPage === 1}
                        className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      <div className="flex items-center gap-1">
                        {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                          const pageNum = i + 1;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                                currentPage === pageNum
                                  ? "bg-green-600 text-white"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() =>
                          setCurrentPage((prev) =>
                            Math.min(totalPages, prev + 1),
                          )
                        }
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Document Preview Modal */}
      {showDocumentModal && selectedDocuments && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Document Preview
                </h3>
                <p className="text-sm text-gray-600 mt-0.5">
                  {selectedDocuments.fullName || "N/A"}
                </p>
              </div>
              <button
                onClick={() => setShowDocumentModal(false)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Aadhar Card Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-gray-900">Aadhar Card</h4>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {selectedDocuments.aadharNumber || "N/A"}
                    </span>
                  </div>

                  {/* Aadhar Front */}
                  {selectedDocuments.documents?.aadharFront ? (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-600">
                        Front Side
                      </p>
                      {selectedDocuments.documents.aadharFront
                        .toLowerCase()
                        .includes(".pdf") ? (
                        <a
                          href={selectedDocuments.documents.aadharFront}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-48 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200 hover:border-blue-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                        >
                          <FileText className="w-12 h-12 text-blue-600" />
                          <span className="text-sm font-medium text-blue-900">
                            View PDF
                          </span>
                        </a>
                      ) : (
                        <a
                          href={selectedDocuments.documents.aadharFront}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={selectedDocuments.documents.aadharFront}
                            alt="Aadhar Front"
                            className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                          />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-400 text-sm">
                        Front not available
                      </p>
                    </div>
                  )}

                  {/* Aadhar Back */}
                  {selectedDocuments.documents?.aadharBack ? (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-600">
                        Back Side
                      </p>
                      {selectedDocuments.documents.aadharBack
                        .toLowerCase()
                        .includes(".pdf") ? (
                        <a
                          href={selectedDocuments.documents.aadharBack}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-48 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200 hover:border-blue-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                        >
                          <FileText className="w-12 h-12 text-blue-600" />
                          <span className="text-sm font-medium text-blue-900">
                            View PDF
                          </span>
                        </a>
                      ) : (
                        <a
                          href={selectedDocuments.documents.aadharBack}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={selectedDocuments.documents.aadharBack}
                            alt="Aadhar Back"
                            className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                          />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-400 text-sm">
                        Back not available
                      </p>
                    </div>
                  )}
                </div>

                {/* PAN Card Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-gray-900">PAN Card</h4>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {selectedDocuments.panNumber || "N/A"}
                    </span>
                  </div>

                  {/* PAN Front */}
                  {selectedDocuments.documents?.panFront ? (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-600">
                        Front Side
                      </p>
                      {selectedDocuments.documents.panFront
                        .toLowerCase()
                        .includes(".pdf") ? (
                        <a
                          href={selectedDocuments.documents.panFront}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-48 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200 hover:border-green-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                        >
                          <FileText className="w-12 h-12 text-green-600" />
                          <span className="text-sm font-medium text-green-900">
                            View PDF
                          </span>
                        </a>
                      ) : (
                        <a
                          href={selectedDocuments.documents.panFront}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={selectedDocuments.documents.panFront}
                            alt="PAN Front"
                            className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-green-500 transition-colors cursor-pointer"
                          />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-400 text-sm">
                        Front not available
                      </p>
                    </div>
                  )}

                  {/* PAN Back */}
                  {selectedDocuments.documents?.panBack ? (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-gray-600">
                        Back Side
                      </p>
                      {selectedDocuments.documents.panBack
                        .toLowerCase()
                        .includes(".pdf") ? (
                        <a
                          href={selectedDocuments.documents.panBack}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-48 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200 hover:border-green-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                        >
                          <FileText className="w-12 h-12 text-green-600" />
                          <span className="text-sm font-medium text-green-900">
                            View PDF
                          </span>
                        </a>
                      ) : (
                        <a
                          href={selectedDocuments.documents.panBack}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={selectedDocuments.documents.panBack}
                            alt="PAN Back"
                            className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-green-500 transition-colors cursor-pointer"
                          />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-400 text-sm">
                        Back not available
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Form 16, Salary Slips, and Selfie Section */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 pt-6 border-t border-gray-200">
                {/* Form 16 */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-gray-900">Form 16</h4>
                  </div>

                  {selectedDocuments.documents?.form16 ? (
                    <div className="space-y-2">
                      {selectedDocuments.documents.form16
                        .toLowerCase()
                        .includes(".pdf") ? (
                        <a
                          href={selectedDocuments.documents.form16}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-64 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border-2 border-purple-200 hover:border-purple-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                        >
                          <FileText className="w-16 h-16 text-purple-600" />
                          <span className="text-sm font-medium text-purple-900">
                            View PDF
                          </span>
                        </a>
                      ) : (
                        <a
                          href={selectedDocuments.documents.form16}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={selectedDocuments.documents.form16}
                            alt="Form 16"
                            className="w-full h-64 object-cover rounded-lg border-2 border-gray-200 hover:border-purple-500 transition-colors cursor-pointer"
                          />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-400 text-sm">
                        Form 16 not available
                      </p>
                    </div>
                  )}
                </div>

                {/* Salary Slips (3 Months) */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-5 h-5 text-orange-600" />
                    <h4 className="font-semibold text-gray-900">
                      Salary Slips (3 Months)
                    </h4>
                  </div>

                  {selectedDocuments.documents?.salarySlip ? (
                    <div className="space-y-2">
                      {selectedDocuments.documents.salarySlip
                        .toLowerCase()
                        .includes(".pdf") ? (
                        <a
                          href={selectedDocuments.documents.salarySlip}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-64 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border-2 border-orange-200 hover:border-orange-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                        >
                          <FileText className="w-16 h-16 text-orange-600" />
                          <span className="text-sm font-medium text-orange-900">
                            View PDF
                          </span>
                        </a>
                      ) : (
                        <a
                          href={selectedDocuments.documents.salarySlip}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={selectedDocuments.documents.salarySlip}
                            alt="Salary Slips"
                            className="w-full h-64 object-cover rounded-lg border-2 border-gray-200 hover:border-orange-500 transition-colors cursor-pointer"
                          />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-400 text-sm">
                        Salary slips not available
                      </p>
                    </div>
                  )}
                </div>

                {/* Selfie */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-5 h-5 text-indigo-600" />
                    <h4 className="font-semibold text-gray-900">Selfie</h4>
                  </div>

                  {selectedDocuments.documents?.selfie ? (
                    <div className="space-y-2">
                      {selectedDocuments.documents.selfie
                        .toLowerCase()
                        .includes(".pdf") ? (
                        <a
                          href={selectedDocuments.documents.selfie}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-64 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border-2 border-indigo-200 hover:border-indigo-500 transition-colors cursor-pointer flex flex-col items-center justify-center gap-3"
                        >
                          <FileText className="w-16 h-16 text-indigo-600" />
                          <span className="text-sm font-medium text-indigo-900">
                            View PDF
                          </span>
                        </a>
                      ) : (
                        <a
                          href={selectedDocuments.documents.selfie}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={selectedDocuments.documents.selfie}
                            alt="Selfie"
                            className="w-full h-64 object-cover rounded-lg border-2 border-gray-200 hover:border-indigo-500 transition-colors cursor-pointer"
                          />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-400 text-sm">
                        Selfie not available
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Info */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h5 className="font-semibold text-sm text-gray-900 mb-3">
                  Additional Information
                </h5>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p className="font-medium text-gray-900">
                      {selectedDocuments.email || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Mobile</p>
                    <p className="font-medium text-gray-900">
                      {selectedDocuments.mobileNumber || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Date of Birth</p>
                    <p className="font-medium text-gray-900">
                      {selectedDocuments.dateOfBirth || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Gender</p>
                    <p className="font-medium text-gray-900 capitalize">
                      {selectedDocuments.gender || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Bank Account</p>
                    <p className="font-medium text-gray-900">
                      {selectedDocuments.bankAccountNumber || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">IFSC Code</p>
                    <p className="font-medium text-gray-900">
                      {selectedDocuments.ifscCode || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowDocumentModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
              >
                Close
              </button>
              <button
                onClick={() =>
                  navigate(`/personal-loans/${selectedDocuments.id}`)
                }
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                View Full Details
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
